import { useMemo, useState } from "react";
import { Clock, Copy, Lock, Pencil, Play, Plus, Trash2 } from "lucide-react";
import { api } from "@/api/client";
import type { TimeEntry } from "@/api/types";
import { useSession, useWorkspaceId } from "@/app/session";
import { useEntriesInfinite, invalidateWorkspace } from "@/features/data";
import { useDeleteEntry, useDuplicateEntry } from "@/features/entryActions";
import { Page } from "@/components/Page";
import { Badge, Button, ColorDot, EmptyState, ErrorState, IconButton, Spinner } from "@/components/ui";
import { EntryDialog, editEntryState, newEntryState, type EntryDialogState } from "@/components/EntryDialog";
import { useToast, errorMessage } from "@/components/Toast";
import { secondsToHMM, formatTimeRange, formatDayHeading } from "@/lib/format";
import { isoDate, startOfWeek } from "@/lib/dates";
import { cn } from "@/lib/cn";

function localDayKey(iso: string): string {
  return isoDate(new Date(iso));
}

interface DayGroup {
  key: string;
  date: Date;
  entries: TimeEntry[];
  total: number;
}
interface WeekSection {
  label: string;
  total: number;
  days: DayGroup[];
}

export function TrackerPage() {
  const wid = useWorkspaceId();
  const { toast } = useToast();
  const weekStart = useSession().me?.user.week_start ?? 1;
  const [dialog, setDialog] = useState<EntryDialogState | null>(null);
  const deleteEntry = useDeleteEntry();
  const duplicateEntry = useDuplicateEntry();

  const { from, to } = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - 30);
    return { from: isoDate(start), to: isoDate(now) };
  }, []);

  const query = useEntriesInfinite(from, to);
  const items = useMemo(() => (query.data?.pages ?? []).flatMap((p) => p.items), [query.data]);
  const sections = useMemo(() => groupEntries(items, weekStart), [items, weekStart]);

  async function resume(entry: TimeEntry) {
    try {
      await api.post(`/workspaces/${wid}/timer/start`, {
        description: entry.description,
        project_id: entry.project_id,
        task_id: entry.task_id,
        rate_class_id: entry.rate_class_id,
        billable: entry.billable,
        tags: entry.tags.map((t) => t.id),
      });
      invalidateWorkspace(wid);
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  }

  return (
    <Page
      title="Tracker"
      description="Your recent time, grouped by week."
      actions={
        <Button size="sm" onClick={() => setDialog(newEntryState())}>
          <Plus className="size-4" /> Add entry
        </Button>
      }
    >
      {query.isError ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : query.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : sections.length === 0 ? (
        <EmptyState
          icon={<Clock className="size-8" />}
          title="No time tracked yet"
          hint="Start the timer above, or press S. Your entries will collect here."
        />
      ) : (
        <div className="space-y-8">
          {sections.map((section) => (
            <section key={section.label}>
              <div className="mb-2 flex items-baseline justify-between border-b border-line pb-1.5">
                <h2 className="text-sm font-semibold text-ink">{section.label}</h2>
                <span className="mono text-sm text-muted">{secondsToHMM(section.total)}</span>
              </div>
              <div className="space-y-4">
                {section.days.map((day) => (
                  <DayBlock
                    key={day.key}
                    day={day}
                    onEdit={(e) => setDialog(editEntryState(e))}
                    onResume={resume}
                    onDuplicate={duplicateEntry}
                    onDelete={deleteEntry}
                  />
                ))}
              </div>
            </section>
          ))}
          {query.hasNextPage && (
            <div className="text-center">
              <Button variant="ghost" size="sm" onClick={() => void query.fetchNextPage()} loading={query.isFetchingNextPage}>
                Load older entries
              </Button>
            </div>
          )}
        </div>
      )}
      {dialog && <EntryDialog state={dialog} onClose={() => setDialog(null)} />}
    </Page>
  );
}

interface RowActions {
  onEdit: (e: TimeEntry) => void;
  onResume: (e: TimeEntry) => void;
  onDuplicate: (e: TimeEntry) => void;
  onDelete: (e: TimeEntry) => void;
}

function DayBlock({ day, ...actions }: { day: DayGroup } & RowActions) {
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between px-1">
        <span className="text-xs font-medium uppercase tracking-wide text-faint">{formatDayHeading(day.date)}</span>
        <span className="mono text-xs text-faint">{secondsToHMM(day.total)}</span>
      </div>
      <div className="overflow-hidden rounded-lg border border-line bg-surface">
        {day.entries.map((entry, i) => (
          <EntryRow key={entry.id} entry={entry} last={i === day.entries.length - 1} {...actions} />
        ))}
      </div>
    </div>
  );
}

function EntryRow({ entry, last, onEdit, onResume, onDuplicate, onDelete }: { entry: TimeEntry; last: boolean } & RowActions) {
  const clock24h = useSession().me?.user.clock_24h ?? true;
  const canEdit = !entry.locked && !entry.running;
  return (
    <div
      className={cn(
        "group flex items-center gap-3 px-3.5 py-2.5",
        !last && "border-b border-line",
        canEdit && "cursor-pointer hover:bg-elevated/40",
      )}
      onClick={() => canEdit && onEdit(entry)}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-ink">
          {entry.details_hidden ? <span className="italic text-faint">Details hidden</span> : entry.description || <span className="text-faint">No description</span>}
        </p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
          {entry.project_name && (
            <span className="inline-flex items-center gap-1.5">
              <ColorDot color={entry.project_color ?? "#888"} />
              {entry.project_name}
              {entry.task_name && <span className="text-faint">· {entry.task_name}</span>}
              {entry.client_name && <span className="text-faint">· {entry.client_name}</span>}
            </span>
          )}
          {entry.tags.map((t) => (
            <Badge key={t.id} className="bg-elevated text-faint">
              {t.name}
            </Badge>
          ))}
          {entry.billable && <span className="text-accent">$</span>}
        </div>
      </div>

      <span className="mono hidden text-xs text-faint sm:block">{formatTimeRange(entry.start_at, entry.end_at, clock24h)}</span>
      <span className={cn("mono w-14 text-right text-sm tabular-nums", entry.running ? "text-running" : "text-ink")}>
        {entry.running ? "· · ·" : secondsToHMM(entry.duration_sec)}
      </span>

      <div
        className="flex items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 max-sm:opacity-100"
        onClick={(ev) => ev.stopPropagation()}
      >
        {entry.locked ? (
          <span title="Locked by a closed month" className="inline-flex size-8 items-center justify-center text-faint">
            <Lock className="size-3.5" />
          </span>
        ) : entry.running ? null : (
          <>
            <IconButton label="Edit" onClick={() => onEdit(entry)}>
              <Pencil className="size-3.5" />
            </IconButton>
            <IconButton label="Duplicate" onClick={() => onDuplicate(entry)}>
              <Copy className="size-3.5" />
            </IconButton>
            <IconButton label="Resume" onClick={() => onResume(entry)}>
              <Play className="size-3.5" />
            </IconButton>
            <IconButton label="Delete" onClick={() => onDelete(entry)} className="hover:text-danger">
              <Trash2 className="size-3.5" />
            </IconButton>
          </>
        )}
      </div>
    </div>
  );
}

function groupEntries(entries: TimeEntry[], weekStart: number): WeekSection[] {
  const now = new Date();
  const thisWeek = startOfWeek(now, weekStart).getTime();
  const lastWeek = thisWeek - 7 * 86400 * 1000;

  const byDay = new Map<string, DayGroup>();
  for (const e of entries) {
    const key = localDayKey(e.start_at);
    let g = byDay.get(key);
    if (!g) {
      const d = new Date(e.start_at);
      g = { key, date: new Date(d.getFullYear(), d.getMonth(), d.getDate()), entries: [], total: 0 };
      byDay.set(key, g);
    }
    g.entries.push(e);
    g.total += e.running ? 0 : e.duration_sec;
  }

  const days = [...byDay.values()].sort((a, b) => b.date.getTime() - a.date.getTime());
  for (const d of days) d.entries.sort((a, b) => Date.parse(b.start_at) - Date.parse(a.start_at));

  const buckets: Record<string, DayGroup[]> = { "This week": [], "Last week": [], Earlier: [] };
  for (const d of days) {
    const t = d.date.getTime();
    if (t >= thisWeek) buckets["This week"].push(d);
    else if (t >= lastWeek) buckets["Last week"].push(d);
    else buckets.Earlier.push(d);
  }

  return (["This week", "Last week", "Earlier"] as const)
    .filter((label) => buckets[label].length > 0)
    .map((label) => ({
      label,
      days: buckets[label],
      total: buckets[label].reduce((s, d) => s + d.total, 0),
    }));
}
